/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { AiOutlineArrowUp } from 'react-icons/ai';
import style from './DataTable.module.css';
import MimaPagination from '../Pagination';
import { useMediaQuery } from 'react-responsive';

const propTypes = {
	overTableSlot: PropTypes.node,
	noItemsViewSlot: PropTypes.node,
	noItemsView: PropTypes.object,
	loadingSlot: PropTypes.node,
	loading: PropTypes.bool,
	striped: PropTypes.bool,
	hover: PropTypes.bool,
	border: PropTypes.bool,
	responsive: PropTypes.bool,
	header: PropTypes.bool,
	items: PropTypes.array,
	headerFields: PropTypes.arrayOf({
		key: PropTypes.string,
		label: PropTypes.string,
		hideOnSm: PropTypes.bool,
		sorter: PropTypes.bool,
		_classes: PropTypes.string,
		_style: PropTypes.object
	}),
	formatedFields: PropTypes.object,
	addTableClasses: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.array,
		PropTypes.object,
	]),

	// Row click
	clickableRows: PropTypes.bool,
	onRowClick: PropTypes.func,

	// Sorting
	sorter: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
	sorterValue: PropTypes.object,
	onSorterValueChange: PropTypes.func,
	onFilteredItemsChange: PropTypes.func,

	// Pagination
	pagination: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
	activePage: PropTypes.number,
	itemsPerPage: PropTypes.number,
	totalCounts: PropTypes.number,
	onPageChange: PropTypes.func,
	onFetchMoreData: PropTypes.func,
};

const defaultProps = {
	itemsPerPage: 300,
	totalCounts: 1000,
	responsive: true,
	hover: true,
	striped: true,
	pagination: true,
	formatedFields: {},
	sorterValue: {},
	header: true,
};

const MimaDataTable = props => {
	const {
		overTableSlot,
		noItemsViewSlot,
		noItemsView,
		loadingSlot,
		formatedFields,
		loading,
		headerFields,
		pagination,
		activePage,
		itemsPerPage,
		totalCounts,
		items,
		sorter,
		header,
		clickableRows,
		addTableClasses,
		striped,
		hover,
		border,
		responsive,
		sorterValue,
		onRowClick,
		onSorterValueChange,
		onPageChange,
		onFilteredItemsChange,
		onFetchMoreData
	} = props;

	const compData = useRef({
		firstRun: true,
		columnFiltered: 0,
		changeItems: 0,
	}).current;

	const isBelow800 = useMediaQuery({
		query: '(max-width: 800px)',
	});

	//
	const [perPageItems, setPerPageItems] = useState(itemsPerPage);
	const [sorterState, setSorterState] = useState(sorterValue || {});
	const [page, setPage] = useState(activePage || 1);
	const [passedItems, setPassedItems] = useState(items || []);
	const [fields, setFields] = useState([]);
	// functions

	const cellClass = (item, colName, index) => {
		let classes = [];
		if (item._cellClasses && item._cellClasses[colName]) {
			classes.push(item._cellClasses[colName]);
		}
		if (fields && fields[index]._classes) {
			classes.push(fields[index]._classes);
		}
		return classes;
	};

	const rowClass = item => {
		let classes = [];

		item._classes && classes.push(item._classes);
		striped && classes.push('even:bg-[#fafafc] odd:bg-white');
		hover && classes.push('hover:bg-gray-100');

		return classes;
	};

	const pretifyName = name => {
		return name
			.replace(/[-_.]/g, ' ')
			.replace(/ +/g, ' ')
			.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
			.split(' ')
			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	};

	const headerClass = i => fields && fields[i]._classes && fields[i]._classes;

	const isSortable = i => {
		const isDataColumn = itemsDataColumns.includes(rawColumnNames[i]);
		return sorter && (!fields || fields[i].sorter !== false) && isDataColumn;
	};

	const headerStyles = index => {
		let style = { verticalAlign: 'middle', overflow: 'hidden' };
		if (isSortable(index)) {
			style.cursor = 'pointer';
		}
		if (fields && fields[index] && fields[index]._style) {
			return { ...style, ...fields[index]._style };
		}
		return style;
	};

	const getIconState = index => {
		const direction = sorterState.asc ? 'asc' : 'desc';
		return rawColumnNames[index] === sorterState.column ? direction : 0;
	};

	const iconClasses = index => {
		const state = getIconState(index);
		return [
			'absolute right-0',
			style['icon-transition'],
			style['arrow-position'],
			!state && 'opacity-40',
			state === 'desc' && style['rotate-icon'],
		];
	};

	const rowClicked = (item, index, e) => {
		onRowClick && onRowClick(item, index, getClickedColumnName(e), e);
	};

	const changeSort = (column, index) => {
		if (!isSortable(index)) {
			return;
		}
		//if column changed or sort was descending change asc to true
		const state = sorterState;
		const columnRepeated = state.column === column;
		if (!sorter || !sorter.resetable) {
			state.column = column;
		} else {
			state.column = columnRepeated && state.asc === false ? null : column;
		}
		state.asc = !(columnRepeated && state.asc);
		setSorterState({ ...state });
	};

	useEffect(() => {
		onSorterValueChange && onSorterValueChange(sorterState);
	}, [JSON.stringify(sorterState)]);

	const getClickedColumnName = (e) => {
		const children = Array.from(e.target.closest('tr').children);
		const clickedCell = children.filter(child => child.contains(e.target))[0];
		return rawColumnNames[children.indexOf(clickedCell)];
	};

	const clean = () => {
		setSorterState({
			column: '',
			asc: true,
		});
	};

	// computed

	const genCols = Object.keys(passedItems[0] || {}).filter(
		el => el.charAt(0) !== '_'
	);

	const rawColumnNames = fields ? fields.map(el => el.key || el) : genCols;

	const itemsDataColumns = rawColumnNames.filter(name =>
		genCols.includes(name)
	);

	useMemo(() => {
		compData.columnFiltered++;
	}, [itemsDataColumns.join(''), compData.changeItems]);

	const columnFiltered = useMemo(() => {
		let items = passedItems;
		return items;
	}, [compData.columnFiltered]);

	const tableFiltered = useMemo(() => {
		let items = columnFiltered;

		return items;
	}, [compData.columnFiltered]);

	const sortedItems = useMemo(() => {
		const col = sorterState.column;
		if (
			!col ||
			!itemsDataColumns.includes(col) ||
			(sorter && sorter.external)
		) {
			return tableFiltered;
		}
		//if values in column are to be sorted by numeric value they all have to be type number
		const flip = sorterState.asc ? 1 : -1;
		const sorted = tableFiltered.slice().sort((item, item2) => {
			const value = item[col];
			const value2 = item2[col];
			const a = typeof value === 'number' ? value : String(value).toLowerCase();
			const b =
				typeof value2 === 'number' ? value2 : String(value2).toLowerCase();
			return a > b ? 1 * flip : b > a ? -1 * flip : 0;
		});
		return sorted;
	}, [
		JSON.stringify(tableFiltered),
		JSON.stringify(sorterState),
		JSON.stringify(sorter),
	]);

	useEffect(() => {
		!compData.firstRun &&
			onFilteredItemsChange &&
			onFilteredItemsChange(sortedItems);
	}, [JSON.stringify(sortedItems)]);

	const tableClasses = [
		'table-auto',
		{
			'border-collapse border border-slate-500': border,
			'w-full': responsive,
		},
		addTableClasses,
	];

	const columnNames = useMemo(() => {
		if (fields) {
			return fields.map(f => {
				return f.label !== undefined ? f.label : pretifyName(f.key || f);
			});
		}
		return rawColumnNames.map(el => pretifyName(el));
	}, [fields, rawColumnNames]);

	const sortingIconStyles = sorter && 'relative pr-4';

	const colspan = rawColumnNames.length;

	const computedPage = useMemo(() => {
		const compPage = pagination ? page : activePage;
		!compData.firstRun && onPageChange && onPageChange(compPage);
		return compPage;
	}, [page, activePage, pagination]);

	const firstItemIndex = (computedPage - 1) * perPageItems || 0;

	const paginatedItems = sortedItems.slice(
		firstItemIndex,
		firstItemIndex + perPageItems
	);

	const currentItems = computedPage ? paginatedItems : sortedItems;

	const noItemsText = (() => {
		const customValues = noItemsView || {};
		if (passedItems.length) {
			return customValues.noResults || 'No filtering results';
		}
		return customValues.noItems || 'No items';
	})();

	useEffect(() => {
		!compData.firstRun &&
			onFetchMoreData &&
			onFetchMoreData({
				limit: perPageItems,
				offSet: firstItemIndex,
			})
	}, [perPageItems, firstItemIndex])

	// watch
	useMemo(() => setPerPageItems(itemsPerPage), [itemsPerPage]);

	useMemo(() => setSorterState({ ...sorterValue }), [sorterValue]);

	useMemo(() => {
		if (isBelow800) {
			setFields(headerFields.filter(x => !x.hideOnSm));
		} else {
			setFields(headerFields);
		}
	}, [headerFields, isBelow800])

	//items
	useMemo(() => {
		if (
			items &&
			!compData.firstRun &&
			(items.length !== passedItems.length ||
				JSON.stringify(items) !== JSON.stringify(passedItems))
		) {
			setPassedItems(items);
			compData.changeItems++;
		}
	});

	// render
	compData.firstRun = false;

	const paginationProps = typeof pagination === 'object' ? pagination : null;

	const headerContent = (
		<tr className="text-xl font-bold text-left text-white bg-[#4a4a68]">
			{columnNames.map((name, index) => {
				return (
					<th
						onClick={() => {
							changeSort(rawColumnNames[index], index);
						}}
						className={classNames([headerClass(index), sortingIconStyles, 'px-8 py-4'])}
						style={headerStyles(index)}
						key={index}
					>
						<div>{name}</div>
						{isSortable(index) &&
							(<AiOutlineArrowUp className={classNames(iconClasses(index))} />)}
					</th>
				);
			})}
		</tr>
	);

	return (
		<React.Fragment>
			{overTableSlot}

			<div className="w-full">
				<table className={classNames(tableClasses)}>
					<thead>{header && headerContent}</thead>
					<tbody
						className="text-xl font-normal text-gray-700"
						style={clickableRows && { cursor: 'pointer' }}>
						{currentItems.map((item, itemIndex) => {
							return (
								<React.Fragment key={itemIndex}>
									<tr
										className={classNames(rowClass(item))}
										tabIndex={clickableRows && 0}
										onClick={e => {
											rowClicked(item, itemIndex + firstItemIndex, e);
										}}
									>
										{rawColumnNames.map((colName, index) => {
											return (
												(formatedFields[colName] &&
													React.cloneElement(
														formatedFields[colName](
															item,
															itemIndex + firstItemIndex
														),
														{
															key: index,
															className: classNames([
																cellClass(item, colName, index),
																`px-8 py-4 ${formatedFields[colName](
																	item, itemIndex + firstItemIndex
																	).props.className}`,
															])
														}
													)) || (
													<td
														className={classNames([
															cellClass(item, colName, index), 'px-8 py-4'
														])}
														key={index}
													>
														{String(item[colName])}
													</td>
												)
											);
										})}
									</tr>
								</React.Fragment>
							);
						})}
						{!currentItems.length && (
							<tr>
								<td colSpan={colspan}>
									{noItemsViewSlot || (
										<div className="text-center my-5">
											<h2>
												{noItemsText + ' '}
											</h2>
										</div>
									)}
								</td>
							</tr>
						)}
					</tbody>
				</table>
				{loading && (loadingSlot || (<div>Loading...</div>))}
			</div>

			{pagination && (
				<MimaPagination
					page={page - 1}
					count={totalCounts}
					onPageChange={(event, newPage) => {
						setPage(newPage + 1);
					}}
					rowsPerPage={perPageItems}
					onRowsPerPageChange={event => {
						setPerPageItems(parseInt(event.target.value, 10));
						setPage(1);
					}}
					{...paginationProps}
				/>
			)}
		</React.Fragment>
	);
};

MimaDataTable.propTypes = propTypes;

MimaDataTable.defaultProps = defaultProps;

MimaDataTable.displayName = 'MimaDataTable';

export default MimaDataTable;
